import {
  Box,
  Typography,
  Modal,
  CircularProgress,
  TextField,
  Checkbox,
  Button as MuiButton,
} from '@mui/material';
import { ExternalLink, FileText, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { globalSnackbar } from 'components/GlobalSnackbar';
import apiReq from 'core/api';
import {
  SourceShell,
  Button as ShellButton,
  IconButton as ShellIconButton,
  EmptyState,
} from 'components/AppShell';
import { USE_NEW_SHELL } from 'core/flags';
import LegacyYandexWikiPage from './YandexWikiPage.legacy';

function YandexWikiPageNew() {
  const YANDEX_AUTH = 'yandex_auth';
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState([]);
  const [addLinkModalOpen, setAddLinkModalOpen] = useState(false);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [savingContext, setSavingContext] = useState(false);
  const [selectedPageIds, setSelectedPageIds] = useState([]);
  const [contextSelectionDirty, setContextSelectionDirty] = useState(false);
  const currentProject = useSelector((state) => state.reducer.project);
  const yandexAuth = localStorage.getItem(YANDEX_AUTH);

  useEffect(() => {
    if (yandexAuth && JSON.parse(yandexAuth)) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  }, [yandexAuth]);

  const fetchWikiPages = useCallback(async () => {
    if (!currentProject?.id) return;

    setLoading(true);
    try {
      const result = await apiReq(`yandex/wiki/pages?project_id=${currentProject.id}`, 'GET');
      const pagesArray = Array.isArray(result) ? result : [];
      setPages(pagesArray);
    } catch (error) {
      globalSnackbar({ message: `Ошибка загрузки страниц: ${error.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [currentProject?.id]);

  useEffect(() => {
    if (!currentProject?.id) {
      setPages([]);
      return;
    }
    setPages([]);
    fetchWikiPages();
  }, [currentProject?.id, fetchWikiPages]);

  useEffect(() => {
    setContextSelectionDirty(false);
    setSelectedPageIds([]);
  }, [currentProject?.id]);

  useEffect(() => {
    if (contextSelectionDirty) {
      setSelectedPageIds((prev) => {
        const validIds = new Set(pages.map((p) => p?.id).filter(Boolean));
        const next = prev.filter((id) => validIds.has(id));
        return next.length === prev.length ? prev : next;
      });
      return;
    }
    const fromServer = pages
      .filter((p) => p?.is_context_enabled)
      .map((p) => p?.id)
      .filter(Boolean);
    setSelectedPageIds(fromServer);
  }, [pages, contextSelectionDirty]);

  const startSyncPolling = (taskId) => {
    setSyncing(true);
    const pollInterval = setInterval(async () => {
      try {
        const status = await apiReq(`yandex/wiki/pages/sync/status?task_id=${taskId}`, 'GET');
        if (status?.status === 'done') {
          clearInterval(pollInterval);
          setSyncing(false);
          globalSnackbar({ message: 'Синхронизация завершена', severity: 'success' });
          fetchWikiPages();
        }
      } catch (error) {
        clearInterval(pollInterval);
        setSyncing(false);
        globalSnackbar({ message: `Ошибка синхронизации: ${error.message}`, severity: 'error' });
      }
    }, 2000);
  };

  const handleSync = async () => {
    if (!currentProject?.id || syncing) return;
    const pageIds = selectedPageIds.filter(Boolean);
    try {
      setSyncing(true);
      const res = await apiReq('yandex/wiki/pages/sync', 'POST', {
        page_ids: pageIds,
        project_id: currentProject.id,
      });
      if (res?.task_id) {
        globalSnackbar({ message: 'Синхронизация начата', severity: 'info' });
        startSyncPolling(res.task_id);
      } else if (pageIds.length === 0) {
        setSyncing(false);
        fetchWikiPages();
      } else {
        setSyncing(false);
        globalSnackbar({ message: 'Не получен task_id для синхронизации', severity: 'error' });
      }
    } catch (error) {
      setSyncing(false);
      globalSnackbar({
        message: `Ошибка запуска синхронизации: ${error.message}`,
        severity: 'error',
      });
    }
  };

  const handleAddLink = async () => {
    if (!newLinkUrl.trim() || !currentProject?.id) return;
    try {
      await apiReq('yandex/wiki/pages/add', 'POST', {
        url: newLinkUrl.trim(),
        project_id: currentProject.id,
      });
      globalSnackbar({ message: 'Ссылка добавлена', severity: 'success' });
      setNewLinkUrl('');
      setAddLinkModalOpen(false);
      fetchWikiPages();
    } catch (error) {
      globalSnackbar({ message: `Ошибка добавления: ${error.message}`, severity: 'error' });
    }
  };

  const openDeleteConfirm = (page) => {
    setDeleteTarget(page);
    setDeleteModalOpen(true);
  };

  const closeDeleteConfirm = () => {
    setDeleteModalOpen(false);
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id || !currentProject?.id) return;
    try {
      await apiReq('yandex/wiki/pages/delete', 'DELETE', {
        id: deleteTarget.id,
        project_id: currentProject.id,
      });
      globalSnackbar({ message: 'Страница удалена', severity: 'success' });
      closeDeleteConfirm();
      fetchWikiPages();
    } catch (error) {
      globalSnackbar({ message: `Ошибка удаления: ${error.message}`, severity: 'error' });
    }
  };

  const handleAuthRedirect = async () => {
    try {
      const response = await apiReq('yandex/oauth/login', 'POST', {
        return_url: `${window.location.origin}/yandex/wiki`,
      });
      if (response.url) {
        window.location.href = response.url;
        return;
      }
      globalSnackbar({ message: 'Ответ от сервера не содержит url', severity: 'info' });
    } catch (error) {
      globalSnackbar({ message: `Ошибка авторизации: ${error.message}`, severity: 'error' });
    }
  };

  const handleLogout = async () => {
    try {
      await apiReq('yandex/oauth/logout', 'POST');
      setIsAuthenticated(false);
      localStorage.removeItem(YANDEX_AUTH);
      globalSnackbar({ message: 'Вы вышли из аккаунта Yandex Wiki', severity: 'info' });
    } catch (error) {
      globalSnackbar({ message: 'Ошибка при выходе из аккаунта', severity: 'error' });
    }
  };

  const handleSaveContext = async () => {
    if (!currentProject?.id || selectedPageIds.length === 0) return;
    setSavingContext(true);
    try {
      await apiReq('yandex/wiki/pages/save', 'POST', {
        project_id: currentProject.id,
        pages: selectedPageIds,
      });
      globalSnackbar({
        message: `Сохранено в контекст: ${selectedPageIds.length}`,
        severity: 'success',
      });
      await fetchWikiPages();
      setContextSelectionDirty(false);
    } catch (error) {
      globalSnackbar({ message: `Ошибка сохранения: ${error.message}`, severity: 'error' });
    } finally {
      setSavingContext(false);
    }
  };

  const handleClearContext = async () => {
    if (!currentProject?.id) return;
    try {
      await apiReq('yandex/wiki/pages/save', 'POST', {
        project_id: currentProject.id,
        pages: [],
      });
      globalSnackbar({
        message: 'Контекст отключён для всех страниц проекта',
        severity: 'info',
      });
      await fetchWikiPages();
      setContextSelectionDirty(false);
    } catch (error) {
      globalSnackbar({ message: `Ошибка: ${error.message}`, severity: 'error' });
    }
  };

  const formatSyncAt = (value) => {
    if (value == null || value === '') return null;
    try {
      let d;
      if (typeof value === 'number') {
        d = value < 1e12 ? new Date(value * 1000) : new Date(value);
      } else if (typeof value === 'object' && value !== null && 'seconds' in value) {
        d = new Date(Number(value.seconds) * 1000);
      } else if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
        const n = Number(value);
        d = n < 1e12 ? new Date(n * 1000) : new Date(n);
      } else {
        d = new Date(value);
      }
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleString('ru-RU');
    } catch {
      return null;
    }
  };

  const toggleSelected = (id, checked) => {
    setContextSelectionDirty(true);
    setSelectedPageIds((prev) =>
      checked ? [...prev, id].filter(Boolean) : prev.filter((x) => x !== id)
    );
  };

  return (
    <>
      <SourceShell
        title="Yandex Wiki"
        subtitle="Страницы Я.Вики, подключённые к проекту"
        actions={
          <>
            <ShellButton
              variant="secondary"
              leftIcon={
                syncing ? (
                  <CircularProgress size={12} sx={{ color: 'currentColor' }} />
                ) : (
                  <RefreshCw size={14} strokeWidth={1.75} />
                )
              }
              onClick={handleSync}
              disabled={!currentProject?.id || syncing || !isAuthenticated}
            >
              {syncing ? 'Синхронизация…' : 'Синхронизировать'}
            </ShellButton>
            <ShellButton
              variant="primary"
              leftIcon={<Plus size={14} strokeWidth={2} />}
              onClick={() => setAddLinkModalOpen(true)}
              disabled={!currentProject?.id || !isAuthenticated}
            >
              Добавить ссылку
            </ShellButton>
          </>
        }
        connection={{
          status: isAuthenticated ? 'connected' : 'disconnected',
          label: 'Yandex',
          onConnect: handleAuthRedirect,
          onDisconnect: handleLogout,
          connectLabel: 'Войти через Яндекс',
        }}
        selection={
          selectedPageIds.length > 0 && contextSelectionDirty
            ? {
                count: selectedPageIds.length,
                hint: `из ${pages.length} страниц`,
                actions: (
                  <>
                    <ShellButton
                      variant="ghost"
                      onClick={() => {
                        setContextSelectionDirty(false);
                        const fromServer = pages
                          .filter((p) => p?.is_context_enabled)
                          .map((p) => p?.id)
                          .filter(Boolean);
                        setSelectedPageIds(fromServer);
                      }}
                    >
                      Сбросить изменения
                    </ShellButton>
                    <ShellButton
                      variant="primary"
                      leftIcon={<Save size={14} strokeWidth={1.75} />}
                      onClick={handleSaveContext}
                      disabled={savingContext}
                    >
                      {savingContext ? 'Сохранение…' : 'Сохранить в контекст'}
                    </ShellButton>
                  </>
                ),
              }
            : undefined
        }
      >
        {!currentProject?.id ? (
          <EmptyState
            title="Выберите проект"
            description="Страницы Wiki привязаны к проекту. Выберите проект в шапке."
          />
        ) : !isAuthenticated ? (
          <EmptyState
            title="Подключите Yandex Wiki"
            description="Нажмите «Войти через Яндекс» в полосе сверху, чтобы получить доступ к страницам."
          />
        ) : (
          <>
            {/* Статусная микро-плашка над списком */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 16px',
                fontSize: 12,
                color: 'var(--shell-text-tertiary)',
                background: 'var(--shell-bg-subtle)',
                borderBottom: '1px solid var(--shell-border-subtle)',
                flexShrink: 0,
              }}
            >
              <span>
                {pages.length === 0
                  ? 'Список пуст'
                  : `${pages.length} страниц · в контексте: ${selectedPageIds.length}`}
              </span>
              <ShellButton
                variant="ghost"
                size="sm"
                onClick={handleClearContext}
                disabled={pages.length === 0 || savingContext}
              >
                Снять все из контекста
              </ShellButton>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={24} sx={{ color: 'var(--shell-accent-primary)' }} />
                </Box>
              ) : pages.length === 0 ? (
                <EmptyState
                  title="Страниц пока нет"
                  description="Добавьте ссылку на страницу Wiki — она появится в списке."
                />
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {pages.map((p) => {
                    const selected = selectedPageIds.includes(p?.id);
                    const at = formatSyncAt(p.last_sync_at);
                    return (
                      <li
                        key={p.id}
                        style={{
                          display: 'flex',
                          gap: 12,
                          padding: '10px 16px',
                          borderBottom: '1px solid var(--shell-border-subtle)',
                          background: selected ? 'var(--shell-accent-subtle)' : 'transparent',
                          transition: 'background var(--shell-transition-fast)',
                        }}
                        onMouseEnter={(e) => {
                          if (!selected)
                            e.currentTarget.style.background = 'var(--shell-bg-subtle)';
                        }}
                        onMouseLeave={(e) => {
                          if (!selected) e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <Checkbox
                          checked={selected}
                          onChange={(e) => toggleSelected(p?.id, e.target.checked)}
                          size="small"
                          sx={{ p: 0.5, mt: 0.25 }}
                        />
                        <div
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: 6,
                            background: selected
                              ? 'var(--shell-accent-primary)'
                              : 'var(--shell-bg-subtle)',
                            color: selected
                              ? 'var(--shell-accent-on-accent)'
                              : 'var(--shell-text-tertiary)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <FileText size={15} strokeWidth={1.75} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: 'var(--shell-text-primary)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {p.title || 'Без названия'}
                          </div>
                          {p.content && (
                            <div
                              style={{
                                fontSize: 12,
                                color: 'var(--shell-text-tertiary)',
                                marginTop: 2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {p.content}
                            </div>
                          )}
                          {(p.is_context_enabled || at) && (
                            <div
                              style={{
                                display: 'flex',
                                gap: 8,
                                alignItems: 'center',
                                marginTop: 6,
                                fontSize: 11,
                                color: 'var(--shell-text-tertiary)',
                              }}
                            >
                              {p.is_context_enabled && (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '1px 8px',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    borderRadius: 999,
                                    background: 'var(--shell-accent-subtle)',
                                    color: 'var(--shell-accent-pressed)',
                                    border: '1px solid var(--shell-accent-primary)',
                                  }}
                                >
                                  В контексте
                                </span>
                              )}
                              {at && <span>Синхронизировано: {at}</span>}
                            </div>
                          )}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            flexShrink: 0,
                          }}
                        >
                          {p.url && (
                            <ShellIconButton
                              size="sm"
                              label="Открыть в новой вкладке"
                              onClick={() => window.open(p.url, '_blank')}
                              icon={<ExternalLink size={14} strokeWidth={1.75} />}
                            />
                          )}
                          <ShellIconButton
                            size="sm"
                            tone="danger"
                            label="Удалить"
                            onClick={() => openDeleteConfirm(p)}
                            icon={<Trash2 size={14} strokeWidth={1.75} />}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}
      </SourceShell>

      <Modal open={addLinkModalOpen} onClose={() => setAddLinkModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 480,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 12px 32px rgba(15,16,20,0.12)',
            borderRadius: 2,
            outline: 'none',
            p: 3,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Добавить ссылку на страницу Wiki
          </Typography>
          <TextField
            label="URL страницы"
            placeholder="https://wiki.yandex.ru/..."
            value={newLinkUrl}
            onChange={(e) => setNewLinkUrl(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2, mt: 1 }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <MuiButton onClick={() => setAddLinkModalOpen(false)}>Отмена</MuiButton>
            <MuiButton
              variant="contained"
              onClick={handleAddLink}
              disabled={!newLinkUrl.trim() || !currentProject?.id}
            >
              Добавить
            </MuiButton>
          </Box>
        </Box>
      </Modal>

      <Modal open={deleteModalOpen} onClose={closeDeleteConfirm}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 12px 32px rgba(15,16,20,0.12)',
            borderRadius: 2,
            outline: 'none',
            p: 3,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Удалить страницу?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            «{deleteTarget?.title || 'Без названия'}» будет отвязана от проекта.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <MuiButton onClick={closeDeleteConfirm}>Отмена</MuiButton>
            <MuiButton variant="contained" color="error" onClick={confirmDelete}>
              Удалить
            </MuiButton>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

function YandexWikiPage() {
  if (!USE_NEW_SHELL) {
    return <LegacyYandexWikiPage />;
  }
  return <YandexWikiPageNew />;
}

export default YandexWikiPage;
